import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { processAction } from 'src/services/WorkflowService'

interface IUnConsumeQuoteModalProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function UnConsumeQuoteModal(props: IUnConsumeQuoteModalProps) {
  const [loading, setLoading] = useState(false)

  const validationSchema = Yup.object({
    comments: Yup.string().when([], {
      is: () => props.action.requireComment,
      then: (schema: any) => schema.required('Comments are required'),
      otherwise: (schema: any) => schema.notRequired(),
    }),
  })

  const formInitialValues = {
    comments: undefined,
  }

  async function handleSubmit(values: any) {
    setLoading(true)
    let data = new ObjectiveProcessAction()
    if (values.comments) {
      data.comment = values.comments
    }
    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish?.(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading(false)
        props.closeModal()
      })
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') props.closeModal()
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Typography
          variant="h5"
          color="primary"
          align="center"
          style={{ marginBottom: 16 }}
        >
          Are you sure you want to unconsume this quote?
        </Typography>
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount={true}
        >
          {({ values, handleChange, errors, touched, isValid, handleBlur }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid item xs={12}>
                  {props.action.displayComment && (
                    <TextField
                      onBlur={handleBlur}
                      inputProps={{ id: 'comments' }}
                      InputLabelProps={{ shrink: true }}
                      type="text"
                      name="comments"
                      variant="outlined"
                      value={values.comments}
                      onChange={handleChange}
                      label="Comments"
                      fullWidth
                      multiline
                      minRows={4}
                      required={props.action.requireComment}
                      error={!!(touched.comments && errors.comments)}
                      helperText={(touched.comments && errors.comments) as string}
                    />
                  )}
                </Grid>
              </Grid>
              <DialogActions>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isValid}
                >
                  Execute
                </Button>
                <Button onClick={props.closeModal} color="primary">
                  Cancel
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default UnConsumeQuoteModal
